import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import DeleteConfirmModal from "./delete-confirm-modal";
import {
  adminGrowthSnapshotDeleteSectionAction,
  adminGrowthSnapshotGetAction,
  adminGrowthSnapshotSectionsListAction
} from "../Action/action";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  const s = String(value);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

export default function GrowthSnapshotSections() {
  const { snapshotId } = useParams();
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [reloadNonce, setReloadNonce] = useState(0);

  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function loadSnapshot() {
      try {
        const res = await adminGrowthSnapshotGetAction(snapshotId);
        if (cancelled) return;
        if (res?.success) setSnapshot(res.data || null);
      } catch (e) {}
    }
    if (snapshotId) loadSnapshot();
    return () => {
      cancelled = true;
    };
  }, [snapshotId]);

  useEffect(() => {
    setPage(1);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    const q = query.trim() ? query.trim() : undefined;

    const t = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const res = await adminGrowthSnapshotSectionsListAction(snapshotId, { search: q });
        if (cancelled) return;
        if (!res?.success) throw new Error(res?.message || "Failed to load sections.");
        setRows(Array.isArray(res.data) ? res.data : []);
        if (res.snapshot) setSnapshot(res.snapshot);
      } catch (e) {
        if (cancelled) return;
        setRows([]);
        setError(e?.response?.data?.message || e?.message || "Failed to load sections.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [snapshotId, query, reloadNonce]);

  const filtered = useMemo(() => rows, [rows]);
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage) || 1);
  const pageRows = useMemo(() => {
    const start = (page - 1) * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, page, perPage]);

  const viewRows = useMemo(() => {
    return pageRows.map((r) => ({
      raw: r,
      id: r?.id,
      title: r?.title || "—",
      phase:
        r?.phase === "beginning" ? "Beginning" : r?.phase === "end" ? "End" : "Both",
      displayOrder: r?.display_order != null && r?.display_order !== "" ? String(r.display_order) : "0",
      stepsCount: Number(r?.steps_count ?? 0),
      statusLabel: Number(r?.status) === 1 ? "Active" : "Inactive",
      created: formatDate(r?.created_at)
    }));
  }, [pageRows]);

  const start = total && viewRows.length ? (page - 1) * perPage + 1 : 0;
  const end = total && viewRows.length ? start + viewRows.length - 1 : 0;

  const pageButtons = useMemo(() => {
    const max = 5;
    const safeTotal = Math.max(1, totalPages);
    const current = Math.min(Math.max(1, page), safeTotal);
    let s = Math.max(1, current - 2);
    let e = Math.min(safeTotal, s + max - 1);
    s = Math.max(1, e - max + 1);
    const arr = [];
    for (let i = s; i <= e; i += 1) arr.push(i);
    return arr;
  }, [page, totalPages]);

  const clearAndReload = () => {
    setQuery("");
    setPage(1);
    setError("");
    setReloadNonce((n) => n + 1);
  };

  const openDelete = (row) => {
    if (!row?.id) return;
    setDeleteRow(row);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);
    try {
      const res = await adminGrowthSnapshotDeleteSectionAction(deleteRow.id);
      if (!res?.success) throw new Error(res?.message || "Delete failed");
      toast.success("Section deleted", { toastId: "gs-section-delete" });
      setDeleteOpen(false);
      setDeleteRow(null);
      setReloadNonce((n) => n + 1);
    } catch (e) {
      toast.error(e?.message || "Delete failed", { toastId: "gs-section-delete" });
    } finally {
      setDeleting(false);
    }
  };

  const snapshotTitle = snapshot?.title || "Growth Snapshot";

  return (
    <div className="content">
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Sections unavailable.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="breadcrumb">
        <Link to="/growth-snapshots">Growth Snapshots</Link>
        <span>›</span>
        <span>{snapshotTitle}</span>
        <span>›</span>
        <span>Sections</span>
      </div>

      <div className="toolbar">
        <div className="flex items-center gap-3">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by title..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <button className="btn btn-outline" type="button" onClick={clearAndReload} disabled={loading}>
            ↻ Clear
          </button>
        </div>

        <div className="flex items-center gap-3">
          <Link to="/growth-snapshots" className="btn btn-outline btn-sm">
            ← Snapshots
          </Link>
          <Link to="new" className="btn btn-primary">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 5v14" />
              <path d="M5 12h14" />
            </svg>
            Add section
          </Link>
        </div>
      </div>

      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Phase</th>
                <th>Display order</th>
                <th>Steps</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7}>
                    <div className="empty-state" style={{ padding: "28px 12px" }}>
                      Loading…
                    </div>
                  </td>
                </tr>
              ) : (
                viewRows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <strong>{r.title}</strong>
                    </td>
                    <td>{r.phase}</td>
                    <td>{r.displayOrder}</td>
                    <td>{r.stepsCount}</td>
                    <td>
                      <span className={`badge ${r.statusLabel === "Active" ? "badge-success" : "badge-gray"}`}>
                        {r.statusLabel}
                      </span>
                    </td>
                    <td>{r.created}</td>
                    <td>
                      <div className="actions-cell">
                        <Link to={`${r.id}/edit`} className="action-link">
                          Edit
                        </Link>
                        <button
                          type="button"
                          className="action-link text-danger"
                          onClick={() => openDelete(r.raw)}
                          style={{ background: "transparent", border: "none", padding: 0 }}
                        >
                          Delete
                        </button>
                        <Link to={`${r.id}/steps`} className="action-link" title="Steps">
                          Steps
                        </Link>
                    
                      </div>
                    </td>
                  </tr>
                ))
              )}
              {!loading && viewRows.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <div className="empty-state">
                      <div className="empty-icon">🫥</div>
                      No sections found.
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <span>
            {total === 0
              ? "No sections"
              : `Showing ${start}–${end} of ${total} Section${total === 1 ? "" : "s"} · Page ${page} of ${totalPages}`}
          </span>
          {total > 0 ? (
            <div className="pagination-pages">
              <button type="button" title="First page" onClick={() => setPage(1)} disabled={page <= 1 || loading}>{"<<"}
              </button>
              <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}>
                ‹
              </button>
              {pageButtons.map((p) => (
                <button
                  key={p}
                  className={p === page ? "active" : ""}
                  type="button"
                  onClick={() => setPage(p)}
                  disabled={loading}
                >
                  {p}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
              >
                ›
              </button>
              <button
                type="button"
                title="Last page"
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages || loading}
              >{">>"}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <DeleteConfirmModal
        open={deleteOpen}
        itemType="section"
        itemName={deleteRow?.title || "Section"}
        onCancel={() => {
          if (deleting) return;
          setDeleteOpen(false);
          setDeleteRow(null);
        }}
        onConfirm={confirmDelete}
        confirmDisabled={deleting}
      />
    </div>
  );
}
